/**
 * Welle 9 (pre89): Bettet beim Export von Ablage-Dokumenten Call-ID + Zeitstempel ein,
 * damit eine Datei später wieder ihrem Gespräch zugeordnet werden kann.
 *
 * - JPEG  → EXIF (ImageDescription, DateTime, Software) — in den Windows-Dateieigenschaften sichtbar
 * - PNG   → tEXt-Chunks (Comment, Software)
 * - Text  → Kopfzeile mit Call-ID/Zeit
 * - PDF   → INHALT BLEIBT UNVERÄNDERT (eine Änderung würde eine vorhandene Signatur brechen);
 *           die Zuordnung trägt hier der Dateiname.
 *
 * Zusätzlich erhält JEDE exportierte Datei einen Dateinamen mit Call-ID + Zeitstempel,
 * sodass die Zuordnung formatunabhängig immer möglich ist.
 *
 * Die gespeicherten IndexedDB-Blobs bleiben unangetastet — gestempelt wird nur die Kopie
 * beim Herunterladen/Speichern.
 */

export interface StampSource {
  callId: string;
  at: number;
  mime: string;
  name: string;
  blob: Blob;
}

const enc = new TextEncoder();

function isoTs(at: number): string {
  return new Date(at).toISOString();
}
function exifDateTime(at: number): string {
  const d = new Date(at);
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}:${p(d.getMonth() + 1)}:${p(d.getDate())} ${p(d.getHours())}:${p(d.getMinutes())}:${p(d.getSeconds())}`;
}
function descText(callId: string, at: number): string {
  return `Bamoa Videochat | Call-ID: ${callId} | Erfasst: ${isoTs(at)}`;
}
const SOFTWARE = 'Bamoa Videochat';

function nulEven(s: string): Uint8Array {
  const b = enc.encode(s);
  let len = b.length + 1;
  if (len % 2 !== 0) len += 1;
  const out = new Uint8Array(len);
  out.set(b);
  return out;
}

// ---------- JPEG EXIF ----------
function buildExif(desc: string, dateTime: string, software: string): Uint8Array {
  const entries = [
    { tag: 0x010e, str: desc },
    { tag: 0x0131, str: software },
    { tag: 0x0132, str: dateTime },
  ].sort((a, b) => a.tag - b.tag);

  const count = entries.length;
  const ifdStart = 8;
  const ifdSize = 2 + count * 12 + 4;
  let dataOffset = ifdStart + ifdSize;

  const strBuffers: Uint8Array[] = [];
  const ev = entries.map((e) => {
    const buf = nulEven(e.str);
    const cnt = enc.encode(e.str).length + 1;
    let offset: number | null = null;
    let valueBytes: Uint8Array | null = null;
    if (buf.length <= 4) {
      valueBytes = new Uint8Array(4);
      valueBytes.set(buf);
    } else {
      offset = dataOffset;
      strBuffers.push(buf);
      dataOffset += buf.length;
    }
    return { tag: e.tag, type: 2, count: cnt, offset, valueBytes };
  });

  const tiff = new Uint8Array(dataOffset);
  const dv = new DataView(tiff.buffer);
  tiff[0] = 0x4d; tiff[1] = 0x4d; dv.setUint16(2, 0x002a); dv.setUint32(4, 8);
  let p = ifdStart;
  dv.setUint16(p, count); p += 2;
  for (const e of ev) {
    dv.setUint16(p, e.tag); p += 2;
    dv.setUint16(p, e.type); p += 2;
    dv.setUint32(p, e.count); p += 4;
    if (e.offset !== null) dv.setUint32(p, e.offset);
    else if (e.valueBytes) tiff.set(e.valueBytes, p);
    p += 4;
  }
  dv.setUint32(p, 0); p += 4;
  let dp = ifdStart + ifdSize;
  for (const sb of strBuffers) { tiff.set(sb, dp); dp += sb.length; }

  const payloadLen = 6 + tiff.length;
  const segLen = payloadLen + 2;
  const app1 = new Uint8Array(4 + payloadLen);
  app1[0] = 0xff; app1[1] = 0xe1;
  app1[2] = (segLen >> 8) & 0xff; app1[3] = segLen & 0xff;
  app1[4] = 0x45; app1[5] = 0x78; app1[6] = 0x69; app1[7] = 0x66; app1[8] = 0; app1[9] = 0;
  app1.set(tiff, 10);
  return app1;
}

function stampJpeg(bytes: Uint8Array, callId: string, at: number): Uint8Array {
  if (!(bytes[0] === 0xff && bytes[1] === 0xd8)) return bytes;
  // Schon ein APP1/EXIF direkt nach SOI? Dann nicht ein zweites einfügen.
  if (bytes[2] === 0xff && bytes[3] === 0xe1) return bytes;
  const app1 = buildExif(descText(callId, at), exifDateTime(at), SOFTWARE);
  const out = new Uint8Array(bytes.length + app1.length);
  out.set(bytes.slice(0, 2), 0);
  out.set(app1, 2);
  out.set(bytes.slice(2), 2 + app1.length);
  return out;
}

// ---------- PNG tEXt ----------
const CRC_TABLE = (() => {
  const t = new Uint32Array(256);
  for (let n = 0; n < 256; n++) {
    let c = n;
    for (let k = 0; k < 8; k++) c = c & 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1;
    t[n] = c >>> 0;
  }
  return t;
})();
function crc32(buf: Uint8Array): number {
  let c = 0xffffffff;
  for (let i = 0; i < buf.length; i++) c = CRC_TABLE[(c ^ buf[i]) & 0xff] ^ (c >>> 8);
  return (c ^ 0xffffffff) >>> 0;
}
function textChunk(keyword: string, text: string): Uint8Array {
  const kw = enc.encode(keyword), tx = enc.encode(text);
  const data = new Uint8Array(kw.length + 1 + tx.length);
  data.set(kw, 0); data[kw.length] = 0; data.set(tx, kw.length + 1);
  const type = enc.encode('tEXt');
  const chunk = new Uint8Array(4 + 4 + data.length + 4);
  const dv = new DataView(chunk.buffer);
  dv.setUint32(0, data.length);
  chunk.set(type, 4); chunk.set(data, 8);
  const crcInput = new Uint8Array(type.length + data.length);
  crcInput.set(type, 0); crcInput.set(data, type.length);
  dv.setUint32(8 + data.length, crc32(crcInput));
  return chunk;
}
function stampPng(bytes: Uint8Array, callId: string, at: number): Uint8Array {
  const sig = [137, 80, 78, 71, 13, 10, 26, 10];
  for (let i = 0; i < 8; i++) if (bytes[i] !== sig[i]) return bytes;
  const ihdrLen = new DataView(bytes.buffer, bytes.byteOffset + 8, 4).getUint32(0);
  const insertAt = 8 + 4 + 4 + ihdrLen + 4;
  const c1 = textChunk('Comment', descText(callId, at));
  const c2 = textChunk('Software', SOFTWARE);
  const out = new Uint8Array(bytes.length + c1.length + c2.length);
  out.set(bytes.slice(0, insertAt), 0);
  out.set(c1, insertAt);
  out.set(c2, insertAt + c1.length);
  out.set(bytes.slice(insertAt), insertAt + c1.length + c2.length);
  return out;
}

// ---------- Dateiname ----------
function fileTs(at: number): string {
  const d = new Date(at);
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}${p(d.getMonth() + 1)}${p(d.getDate())}-${p(d.getHours())}${p(d.getMinutes())}${p(d.getSeconds())}`;
}
function safeId(callId: string): string {
  return callId.replace(/[^a-zA-Z0-9_-]/g, '');
}
/** Für Windows / File System Access API unzulässige Zeichen ersetzen. */
export function sanitizeFilename(name: string): string {
  let s = name.replace(/[\\/:*?"<>|\u0000-\u001f]/g, '-');
  s = s.replace(/\s+/g, ' ').trim();
  s = s.replace(/^\.+/, '').replace(/[ .]+$/, '');
  return s || 'datei';
}
export function decorateName(name: string, callId: string, at: number): string {
  const dot = name.lastIndexOf('.');
  const base = dot > 0 ? name.slice(0, dot) : name;
  const ext = dot > 0 ? name.slice(dot) : '';
  return sanitizeFilename(`${base}__call-${safeId(callId)}__${fileTs(at)}`) + ext;
}

// ---------- Haupteinstieg ----------
export async function stampForExport(d: StampSource): Promise<{ blob: Blob; name: string }> {
  const mime = (d.mime || '').toLowerCase();
  let blob = d.blob;
  try {
    if (mime.includes('jpeg') || mime.includes('jpg')) {
      const bytes = new Uint8Array(await d.blob.arrayBuffer());
      blob = new Blob([stampJpeg(bytes, d.callId, d.at) as unknown as BlobPart], { type: d.mime });
    } else if (mime.includes('png')) {
      const bytes = new Uint8Array(await d.blob.arrayBuffer());
      blob = new Blob([stampPng(bytes, d.callId, d.at) as unknown as BlobPart], { type: d.mime });
    } else if (mime.startsWith('text/')) {
      const header = `Bamoa Videochat — Call-ID: ${d.callId} — Erfasst: ${isoTs(d.at)}\n\n`;
      const orig = await d.blob.text();
      blob = new Blob([header + orig], { type: d.mime });
    }
    // PDF & sonstige: Inhalt unverändert (Signatur-Schutz)
  } catch (e) {
    console.warn('[docMeta] Einbettung fehlgeschlagen, Datei unverändert', e);
    blob = d.blob;
  }
  return { blob, name: decorateName(d.name, d.callId, d.at) };
}
